import { z } from 'zod'
import { api } from '@/shared/api/client'
import { syntheticTotal } from '@/shared/api/pageResult'
import { instantSchema, moneySchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import {
  DISCOUNT_DECISIONS,
  actorRefSchema,
  branchRefSchema,
  pageOf,
  type ActorRef,
  type BranchRef,
  type DiscountDecision,
} from './cashdeskApi'

export const DISCOUNT_STATES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED', 'POSTED', 'REVERSED'] as const
export type DiscountState = (typeof DISCOUNT_STATES)[number]

export const DISCOUNT_STATE_COLOR: Record<DiscountState, string> = {
  PENDING: 'gold',
  APPROVED: 'blue',
  REJECTED: 'red',
  CANCELLED: 'default',
  POSTED: 'green',
  REVERSED: 'orange',
}

export const BASIS_POINTS = 10_000


const policySchema = z.object({
  roleId: uuidSchema,
  maxPercentBp: z.number().nullish(),
  maxAmount: z.number().nullish(),
  approvalAbove: z.boolean(),
})

export interface DiscountPolicyLimit {
  roleId: string
  maxPercentBp: number | null
  maxAmount: number | null
  approvalAbove: boolean
}

function toPolicyLimit(raw: z.infer<typeof policySchema>): DiscountPolicyLimit {
  return {
    roleId: raw.roleId,
    maxPercentBp: raw.maxPercentBp ?? null,
    maxAmount: raw.maxAmount ?? null,
    approvalAbove: raw.approvalAbove,
  }
}

const quoteSchema = z.object({
  planId: uuidSchema,
  planName: z.string().nullish(),
  price: moneySchema,
  balance: moneySchema,
  debtLimit: moneySchema,
  discountPolicies: z.array(policySchema),
  canApplyDiscount: z.boolean(),
  canSellOnCredit: z.boolean(),
})

export interface SaleQuote {
  planId: string
  planName: string | null
  price: Money
  balance: Money
  debtLimit: Money
  discountPolicies: DiscountPolicyLimit[]
  canApplyDiscount: boolean
  canSellOnCredit: boolean
}

export function parseQuote(data: unknown): SaleQuote {
  const raw = quoteSchema.parse(data)
  return {
    planId: raw.planId,
    planName: raw.planName ?? null,
    price: toMoney(raw.price),
    balance: toMoney(raw.balance),
    debtLimit: toMoney(raw.debtLimit),
    discountPolicies: raw.discountPolicies.map(toPolicyLimit),
    canApplyDiscount: raw.canApplyDiscount,
    canSellOnCredit: raw.canSellOnCredit,
  }
}

export async function fetchQuote(
  subscriberId: string,
  planId: string,
  branchId: string | null,
  signal?: AbortSignal,
): Promise<SaleQuote> {
  const response = await api.get<unknown>('/api/cashdesk/quote', {
    ...(signal ? { signal } : {}),
    params: { subscriberId, planId, ...(branchId ? { branchId } : {}) },
  })
  return parseQuote(response.data)
}

export interface SalePreview {
  balanceAfter: number
  outstanding: number
  debtIncrease: number
  /** Qarz abonentning qarz limitidan oshadi — sotuv o'tmaydi (ADR-057) */
  overLimit: boolean
}

export function salePreview(input: {
  balance: number
  debtLimit: number
  price: number
  paid: number
  discount: number
}): SalePreview {
  const balanceAfter = input.balance + input.paid + input.discount - input.price
  const outstanding = Math.max(0, -balanceAfter)
  const debtIncrease = Math.max(0, outstanding - Math.max(0, -input.balance))
  return {
    balanceAfter,
    outstanding,
    debtIncrease,
    overLimit: debtIncrease > 0 && outstanding > input.debtLimit,
  }
}

export type DiscountVerdict = 'NONE' | 'WITHIN' | 'APPROVAL' | 'FORBIDDEN'

export function discountVerdict(
  policies: readonly DiscountPolicyLimit[],
  base: number,
  discount: number,
): DiscountVerdict {
  if (discount <= 0) return 'NONE'
  const within = policies.some(
    (p) =>
      (p.maxPercentBp === null || discount * BASIS_POINTS <= p.maxPercentBp * base) &&
      (p.maxAmount === null || discount <= p.maxAmount),
  )
  if (within) return 'WITHIN'
  return policies.length === 0 || policies.some((p) => p.approvalAbove) ? 'APPROVAL' : 'FORBIDDEN'
}

export interface SaleState {
  verdict: DiscountVerdict
  preview: SalePreview | null
  blocked: boolean
}

export function saleState(
  quote: SaleQuote | undefined,
  paid: number,
  discount: number,
  preApproved: boolean,
  allowCredit: boolean,
): SaleState {
  if (!quote) return { verdict: 'NONE', preview: null, blocked: false }
  const verdict = preApproved ? 'WITHIN' : discountVerdict(quote.discountPolicies, quote.price.amount, discount)
  const preview = salePreview({
    balance: quote.balance.amount,
    debtLimit: quote.debtLimit.amount,
    price: quote.price.amount,
    paid,
    discount,
  })
  const blocked =
    verdict === 'APPROVAL' ||
    verdict === 'FORBIDDEN' ||
    discount > quote.price.amount ||
    (preview.debtIncrease > 0 && (!allowCredit || !quote.canSellOnCredit || preview.overLimit))
  return { verdict, preview, blocked }
}

export function percentOf(amount: number, base: number): number {
  return base > 0 ? Math.round((amount * BASIS_POINTS) / base) : 0
}


export const discountSchema = z.object({
  id: uuidSchema,
  state: z.enum(DISCOUNT_STATES),
  decision: z.enum(DISCOUNT_DECISIONS),
  subscriber: z.object({ id: uuidSchema, login: z.string(), name: z.string().nullish() }),
  branch: branchRefSchema,
  planId: uuidSchema.nullish(),
  planName: z.string().nullish(),
  invoiceId: uuidSchema.nullish(),
  invoiceNo: z.string().nullish(),
  base: moneySchema,
  amount: moneySchema,
  percentBp: z.number(),
  reason: z.string(),
  requestedBy: actorRefSchema,
  requestedAt: instantSchema,
  decidedBy: actorRefSchema.nullish(),
  decidedAt: instantSchema.nullish(),
  decisionNote: z.string().nullish(),
  cancelledBy: actorRefSchema.nullish(),
  cancelledAt: instantSchema.nullish(),
  cancelReason: z.string().nullish(),
  receiptId: uuidSchema.nullish(),
  receiptNo: z.string().nullish(),
  postedAt: instantSchema.nullish(),
  reversedAt: instantSchema.nullish(),
  reverseReason: z.string().nullish(),
})

export interface Discount {
  id: string
  state: DiscountState
  decision: DiscountDecision
  subscriber: { id: string; login: string; name: string | null }
  branch: BranchRef
  planId: string | null
  planName: string | null
  invoiceId: string | null
  invoiceNo: string | null
  base: Money
  amount: Money
  percentBp: number
  reason: string
  requestedBy: ActorRef
  requestedAt: string
  decidedBy: ActorRef | null
  decidedAt: string | null
  decisionNote: string | null
  cancelledBy: ActorRef | null
  cancelledAt: string | null
  cancelReason: string | null
  receiptId: string | null
  receiptNo: string | null
  postedAt: string | null
  reversedAt: string | null
  reverseReason: string | null
}

export function toDiscount(raw: z.infer<typeof discountSchema>): Discount {
  return {
    ...raw,
    subscriber: { id: raw.subscriber.id, login: raw.subscriber.login, name: raw.subscriber.name ?? null },
    planId: raw.planId ?? null,
    planName: raw.planName ?? null,
    invoiceId: raw.invoiceId ?? null,
    invoiceNo: raw.invoiceNo ?? null,
    base: toMoney(raw.base),
    amount: toMoney(raw.amount),
    decidedBy: raw.decidedBy ?? null,
    decidedAt: raw.decidedAt ?? null,
    decisionNote: raw.decisionNote ?? null,
    cancelledBy: raw.cancelledBy ?? null,
    cancelledAt: raw.cancelledAt ?? null,
    cancelReason: raw.cancelReason ?? null,
    receiptId: raw.receiptId ?? null,
    receiptNo: raw.receiptNo ?? null,
    postedAt: raw.postedAt ?? null,
    reversedAt: raw.reversedAt ?? null,
    reverseReason: raw.reverseReason ?? null,
  }
}

export interface DiscountFilter {
  state: DiscountState | null
  branchId?: string | null
  subscriberId?: string | null
}

function discountParams(filter: DiscountFilter): Record<string, string> {
  return {
    ...(filter.state ? { state: filter.state } : {}),
    ...(filter.branchId ? { branchId: filter.branchId } : {}),
    ...(filter.subscriberId ? { subscriberId: filter.subscriberId } : {}),
  }
}

export function fetchDiscountPage(
  scope: 'queue' | 'mine',
  filter: DiscountFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<Discount>> {
  const path = scope === 'mine' ? '/api/cashdesk/discounts/mine' : '/api/cashdesk/discounts'
  return async (state, signal) => {
    const response = await api.get<unknown>(path, {
      signal,
      params: { ...discountParams(filter), page: state.page - 1, size: state.pageSize },
    })
    const parsed = pageOf(discountSchema).parse(response.data)
    return {
      items: parsed.rows.map(toDiscount),
      total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
    }
  }
}

export async function fetchMyApprovedDiscounts(subscriberId: string, signal?: AbortSignal): Promise<Discount[]> {
  const response = await api.get<unknown>('/api/cashdesk/discounts/mine', {
    ...(signal ? { signal } : {}),
    params: { state: 'APPROVED', subscriberId, page: 0, size: 50 },
  })
  return pageOf(discountSchema).parse(response.data).rows.map(toDiscount)
}

export interface DiscountRequestDraft {
  subscriberId: string
  branchId: string | null
  planId: string
  amount: number
  reason: string
}

export async function requestDiscount(draft: DiscountRequestDraft, idempotencyKey: string): Promise<Discount> {
  const response = await api.post<unknown>(
    '/api/cashdesk/discounts',
    {
      subscriberId: draft.subscriberId,
      ...(draft.branchId ? { branchId: draft.branchId } : {}),
      planId: draft.planId,
      amount: draft.amount,
      reason: draft.reason,
    },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return toDiscount(discountSchema.parse(response.data))
}

export type DiscountAction = 'approve' | 'reject' | 'cancel'

export async function decideDiscount(id: string, action: DiscountAction, text: string | null): Promise<Discount> {
  const body = action === 'approve' ? (text ? { note: text } : {}) : { reason: text ?? '' }
  const response = await api.post<unknown>(`/api/cashdesk/discounts/${id}/${action}`, body)
  return toDiscount(discountSchema.parse(response.data))
}


const policyViewSchema = policySchema.extend({
  updatedBy: actorRefSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface DiscountPolicy extends DiscountPolicyLimit {
  updatedBy: ActorRef | null
  updatedAt: string | null
}

function toPolicy(raw: z.infer<typeof policyViewSchema>): DiscountPolicy {
  return { ...toPolicyLimit(raw), updatedBy: raw.updatedBy ?? null, updatedAt: raw.updatedAt ?? null }
}

export async function fetchDiscountPolicies(signal?: AbortSignal): Promise<DiscountPolicy[]> {
  const response = await api.get<unknown>('/api/cashdesk/discount-policies', { ...(signal ? { signal } : {}) })
  return z.array(policyViewSchema).parse(response.data).map(toPolicy)
}

export async function saveDiscountPolicy(policy: DiscountPolicyLimit): Promise<void> {
  await api.put(`/api/cashdesk/discount-policies/${policy.roleId}`, {
    maxPercentBp: policy.maxPercentBp,
    maxAmount: policy.maxAmount,
    approvalAbove: policy.approvalAbove,
  })
}

export async function deleteDiscountPolicy(roleId: string): Promise<void> {
  await api.delete(`/api/cashdesk/discount-policies/${roleId}`)
}
